import { AuthenticatedHttpClient } from "@/lib/axios";
import { LabelType } from "@/types/common";
import { ILabel } from "@/types/label.types";
import { AxiosResponse } from "axios";

export interface Label {
  _id: string;
  name: string;
  description?: string;
  color?: string;
  createdBy: string;
  updatedBy?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateLabelDto {
  name: string;
  description?: string;
  color?: string;
  labelType: LabelType;
}

export interface UpdateLabelDto {
  name?: string;
  description?: string;
  color?: string;
  labelType?: LabelType;
}

export interface PaginationParams {
  page?: number;
  limit?: number;
  labelType?: LabelType;
}

export interface BulkUpdatePayload {
  ids: string[];
  updates: UpdateLabelDto;
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export class LabelService {
  private http: AuthenticatedHttpClient;

  constructor(httpClient: AuthenticatedHttpClient) {
    this.http = httpClient;
  }

  // Get all labels with pagination
  async getLabels(params?: PaginationParams) {
    const response = await this.http.get(
      `/labels?page=${params?.page}&limit=${params?.limit}&labelType=${params?.labelType}`
    );
    return response as unknown as PaginatedResponse<ILabel>;
  }

  // Get single label by ID
  async getLabelById(id: string): Promise<ILabel> {
    const response = await this.http.get<ILabel>(`/labels/${id}`);
    return response.data;
  }

  // Create new label
  async createLabel(data: CreateLabelDto): Promise<ILabel> {
    const response = await this.http.post<ILabel>("/labels", { data });
    return response as unknown as ILabel;
  }

  // Update label
  async updateLabel(id: string, data: UpdateLabelDto): Promise<ILabel> {
    const response = await this.http.patch<ILabel>(`/labels/${id}`, data);
    return response as unknown as ILabel;
  }

  // Delete label
  async deleteLabel(id: string): Promise<void> {
    await this.http.delete(`/labels/${id}`);
  }

  // Bulk delete labels
  async bulkDeleteLabels(ids: string[]): Promise<{ deletedCount: number }> {
    const response = await this.http.post<{ deletedCount: number }>(
      "/labels/bulk-delete",
      {
        ids,
      }
    );
    return response.data;
  }

  // Bulk update labels
  async bulkUpdateLabels(
    payload: BulkUpdatePayload
  ): Promise<{ modifiedCount: number }> {
    const response = await this.http.post<{ modifiedCount: number }>(
      "/labels/bulk-update",
      payload
    );
    return response.data;
  }
}
